import Client from '../Client';
import _ from 'lodash';

class RestClient extends Client {
    constructor(config) {
        super();

        this.LIST_PATH   = '/list';
        this.GET_PATH    = '/get/id';
        this.UPDATE_PATH = '/update/id';
        this.CREATE_PATH = '/create';
        this.DELETE_PATH = '/delete/id';

        this.entryPoint = config.entryPoint;
        this.resource   = config.resource;
        this.resources  = config.resources
        this.model      = config.model;
        this.idField    = config.idField || 'id';
        this.sortBy     = config.sortBy;
    }

    _normalizeFieldErrors(rawErrors = {}) {
        return Object.keys(rawErrors || {}).reduce((memo, key) => {
            const value = rawErrors[key];
            if (Array.isArray(value)) {
                memo[key] = value.map(v => String(v));
            } else if (value !== null && typeof value !== 'undefined') {
                memo[key] = [String(value)];
            }
            return memo;
        }, {});
    }

    _buildApiError(json, fallbackMessage = 'Request failed') {
        const errorPayload = json?.flash?.error;
        const modelErrors = this._normalizeFieldErrors(errorPayload?.errors || {});
        const invalidRequestErrors = this._normalizeFieldErrors(json?.flash?.invalid || {});
        const fieldErrors = { ...modelErrors, ...invalidRequestErrors };

        let message = fallbackMessage;
        if (typeof errorPayload === 'string' && errorPayload.trim() !== '') {
            message = errorPayload;
        } else if (errorPayload && typeof errorPayload === 'object') {
            const parts = [];
            if (errorPayload.msg) {
                parts.push(String(errorPayload.msg));
            }
            const details = Object.keys(modelErrors).map(key => `${key}: ${modelErrors[key].join(', ')}`);
            if (details.length > 0) {
                parts.push(details.join(' - '));
            }
            if (parts.length > 0) {
                message = parts.join(' : ');
            }
        }

        if ((message === fallbackMessage || !message) && Object.keys(invalidRequestErrors).length > 0) {
            const details = Object.keys(invalidRequestErrors)
                .map(key => `${key}: ${invalidRequestErrors[key].join(', ')}`)
                .join(' - ');
            if (details) {
                message = details;
            }
        }

        const err = new Error(message || fallbackMessage);
        err.fieldErrors = fieldErrors;
        return err;
    }

    list(filters) {
        // remove empty filter values (and empty arrays)
        filters = _.pickBy(filters, (v) => Array.isArray(v) ? v.length > 0 : _.identity(v));

        // stringify fields
        const fields = (this.model.getListingFields() || []).join(',');

        return this.GET(`/${this.entryPoint}${this.LIST_PATH}`, { ...filters, fields })
            .then(response => response.json())
            .then(json => {
                if (json[this.resources]) {
                    json[this.resources] = json[this.resources].map(r => new this.model(r));

                    if (this.sortBy)
                        json[this.resources] = _.sortBy(json[this.resources], this.sortBy);

                    return json;
                }
                throw this._buildApiError(json, `Unable to list ${this.resources}`);
            });
    }

    get(id) {
        return this.GET(`/${this.entryPoint}${this.GET_PATH}/${id}`)
            .then(response => response.json())
            .then(json => {
                if (json[this.resource]) {
                    json[this.resource] = new this.model(json[this.resource]);
                    return json;
                }
                throw this._buildApiError(json, `Unable to load ${this.resource}`);
            });
    }

    create(payload) {
        return this.POST(`/${this.entryPoint}${this.CREATE_PATH}`, { [this.resource]: payload })
            .then(response => response.json())
            .then(json => {
                if (json[this.resource]) {
                    json[this.resource] = new this.model(json[this.resource]);
                    return json;
                }
                throw this._buildApiError(json, `Unable to create ${this.resource}`);
            });
    }

    update(payload) {
        return this.PUT(
                `/${this.entryPoint}${this.UPDATE_PATH}/${payload[this.idField]}`,
                { [this.resource]: payload }
            ).then(response => response.json())
            .then(json => {
                if (json[this.resource]) {
                    json[this.resource] = new this.model(json[this.resource]);
                    return json;
                }
                throw this._buildApiError(json, `Unable to update ${this.resource}`);
            });
    }

    delete(id) {
        return this.POST(`/${this.entryPoint}${this.DELETE_PATH}/${id}`, {})
            .then(response => response.json())
            .then(json => {
                if (json.flash && json.flash.error)
                    throw this._buildApiError(json, `Unable to delete ${this.resource}`);

                return json;
            });
    }
}

export default RestClient;
